/**
 * Shared constants for the contracts feature.
 *
 * Centralizes user-facing strings, MIME type allow-lists, and default
 * drawer state so the rest of the feature can stay declarative.
 * @module features/contracts/constants
 */

import type { ContractDocumentType, ContractDrawerMode } from "./types";

/** MIME types accepted by the contract upload zone. */
export const CONTRACT_ACCEPTED_MIME_TYPES: readonly string[] = [
  "application/pdf",
  "image/jpeg",
  "image/png",
];

/** Privacy disclaimer shown above the upload action buttons. */
export const CONTRACT_PRIVACY_DISCLAIMER =
  "For privacy, uploaded documents are not stored long-term and are automatically deleted after 24 hours.";

/** Default document type when the drawer first opens. */
export const CONTRACT_DRAWER_DEFAULT_DOCUMENT_TYPE: ContractDocumentType =
  "CONTRACT";

/**
 * Default mode when the drawer first opens. The list view renders the
 * empty state internally when the player has no uploaded documents, so
 * opening the drawer always starts in list mode regardless of whether
 * the player has documents yet.
 */
export const CONTRACT_DRAWER_DEFAULT_MODE: ContractDrawerMode = "list";

/**
 * Fallback copy rendered in the preview pane when the document's content
 * type is neither a supported PDF nor a supported image MIME type.
 */
export const CONTRACT_PREVIEW_UNSUPPORTED_MESSAGE =
  "This file type can't be previewed. Please download the file to view it.";

/**
 * Title displayed in the drawer header for each document type. Wave 3
 * uses this map when rendering the upload section title.
 */
export const CONTRACT_DRAWER_TITLE_BY_DOCUMENT_TYPE: Readonly<
  Record<ContractDocumentType, string>
> = {
  CONTRACT: "Upload Contract Documents",
  TRANSFER_AGREEMENT: "Upload Transfer Agreement",
};

/** Polling interval (ms) while waiting for extraction to complete. */
export const EXTRACTION_POLL_INTERVAL_MS = 4000;

/**
 * Maximum duration (ms) before polling gives up and shows a timeout
 * error. Matches the backend Lambda's worst-case runtime (900s) so a
 * legitimate long extraction doesn't surface a spurious "taking longer
 * than expected" banner while the server is still working.
 */
export const EXTRACTION_MAX_POLL_DURATION_MS = 900_000;

/**
 * Canonical display order for extraction category keys. `Object.keys`
 * order is not guaranteed stable across refetches or upstream
 * serialization boundaries; without this order the Contract drawer,
 * mobile page, and Player Page Financials tab could each render the
 * same data with inconsistent section ordering. Callers filter the
 * keys they encounter through this list and append any unknown keys
 * last so new backend categories degrade gracefully into a stable
 * "everything else" bucket.
 *
 * Architecture risk mitigation flagged in Task #1 scoping for SE-4347.
 */
export const EXTRACTION_CATEGORY_ORDER: readonly string[] = [
  "fixed_terms",
  "variable_bonuses",
  "clauses",
  "other",
];

/**
 * Canonical key for the dedicated Contract End Date section (SE-4881).
 * Rendered FIRST in the extraction view as a synthetic section derived
 * from `extraction.endDate` (NOT from an `extractedData` category).
 *
 * Deliberately NOT a member of `EXTRACTION_CATEGORY_ORDER`: its leading
 * position comes from `parseCategorySummaries` PREPENDING a synthetic
 * summary (via `includeContractEndDate`), and the key is always
 * suppressed from the derived `extractedData` categories so it can never
 * render twice — adding it to the order array would be dead, confusing
 * config. `formatCategoryDisplayName` maps this key to "Contract End
 * Date", which is the section's visible label.
 */
export const CONTRACT_END_DATE_CATEGORY_KEY = "contract_end_date";

/**
 * Legacy `extractedData` keys that historically held the contract end
 * date as a free-text line item before SE-4881 introduced the dedicated
 * `endDate` field. Stripped from the rendered category list so historical
 * rows don't show the end date twice (once in the dedicated section, once
 * as a stale free-text category).
 */
export const LEGACY_END_DATE_CATEGORY_KEYS: readonly string[] = [
  "end_date",
  "endDate",
];

/**
 * Empty-state copy rendered by `ContractEndDateSection` when the
 * extraction has no `endDate`. Matches the generic "no data" phrasing
 * used elsewhere in the extraction surfaces.
 */
export const CONTRACT_END_DATE_EMPTY_MESSAGE =
  "No information found in this category.";
